import EChartCustomized, {
    SCHEMES,
    TOOLTIP_OPTIONS,
} from '@/components/EchartCustomized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getServiceAnalyticUsages } from '@/modules/Service/services'
import { ServiceMonitoringProps } from '@/modules/Service/types'
import {
    buildYearOptions,
    formatBinaryStorageUnit,
    formatNumber,
} from '@/utils/function'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { Select, Typogaphy } from 'vnetwork-ui'

// LFD (Large File Delivery) stores on Conversant, not Wasabi — so it has no
// per-bucket breakdown, only a monthly stored-bytes figure. This view mirrors
// the OSS Business Usage layout (header + picker, KPI tile, card) but renders a
// year-of-months bar chart instead of a bucket table. Storage is binary GiB
// (1024³), matching the OSS page — the old view mislabeled it as bandwidth (bps).

interface StorageSeriesPoint {
    timestamp: string
    value: number
}
interface StorageSeries {
    metric?: string
    granularity?: string
    series?: StorageSeriesPoint[]
}

const fmtBytes = (v?: number) =>
    formatBinaryStorageUnit(v ?? 0, 2, 'XiB', false)

const Monitoring: React.FC<ServiceMonitoringProps> = ({ service }) => {
    const serviceID = service?.service_id as number
    const [year, setYear] = useState(dayjs().year().toString())

    const { data } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES, serviceID, 'storage-lfd', year],
        queryFn: () =>
            getServiceAnalyticUsages({
                serviceID,
                params: {
                    type: '/storage',
                    gte: `${year}-01-01`,
                    lte: `${year}-12-31`,
                },
            }),
        enabled: !!serviceID,
    })

    const series = ((data ?? {}) as StorageSeries).series ?? []

    // Bucket the year's points into 12 month slots (bytes) so the chart always
    // shows a full Jan–Dec axis; months the vendor has no row for stay 0.
    const monthly = useMemo(() => {
        const slots = Array<number>(12).fill(0)
        series.forEach((p) => {
            const m = dayjs(p.timestamp).month() // 0-11
            if (m >= 0 && m < 12) slots[m] += p.value || 0
        })
        return slots
    }, [series])

    const peak = useMemo(() => Math.max(0, ...monthly), [monthly])
    const monthsWithData = useMemo(
        () => monthly.filter((v) => v > 0).length,
        [monthly]
    )

    const options = useMemo(
        () => ({
            tooltip: {
                trigger: 'axis',
                ...TOOLTIP_OPTIONS,
                formatter: (params: any) => {
                    const [cur] = params
                    return `<b>${cur?.axisValue} ${year}</b><br/>${
                        cur?.value ? fmtBytes(cur.value) : '-'
                    }`
                },
            },
            grid: {
                top: '12%',
                left: '2%',
                right: '2%',
                bottom: '5%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                data: Array.from({ length: 12 }, (_, i) =>
                    dayjs().month(i).format('MMM')
                ),
            },
            yAxis: {
                type: 'value',
                splitLine: {
                    show: true,
                    lineStyle: { color: '#e0e0e0', type: [3, 3] },
                },
                axisLabel: {
                    formatter: (v: number) => fmtBytes(v).replace('.00', ''),
                },
            },
            color: [SCHEMES.primary[0]],
            series: [{ type: 'bar', data: monthly }],
        }),
        [monthly, year]
    )

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Typogaphy size="medium" className="text-primary font-semibold">
                    Storage Monthly Usage
                </Typogaphy>
                <div className="w-[180px]">
                    <Select
                        value={year}
                        onChange={(v) => setYear(v as string)}
                        options={buildYearOptions({ start: 2013 }).reverse()}
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    {
                        label: 'Peak stored (billed)',
                        value: fmtBytes(peak),
                        hint: `${formatNumber(
                            Math.round(peak / (1024 * 1024 * 1024))
                        )} GiB billed`,
                    },
                    {
                        label: 'Months with data',
                        value: `${monthsWithData}/12`,
                        hint: '',
                    },
                ].map((k) => (
                    <div key={k.label} className="bg-white rounded-[8px] p-4">
                        <div className="text-xs text-gray-500">{k.label}</div>
                        <div className="text-lg font-semibold text-primary">
                            {k.value}
                        </div>
                        {k.hint && (
                            <div className="text-xs text-gray-400">
                                {k.hint}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-[8px] p-4">
                {/* @ts-ignore EChartCustomized options typing is loose */}
                <EChartCustomized options={options} />
            </div>
        </div>
    )
}

export default Monitoring
